import { Form, Input, Modal } from "antd";
import { useEffect, useState } from "react";
import { useCreateTerritory, useUpdateTerritory } from "../hooks/useTerritories";
import type { Territory, TerritoryCoordinates } from "../services/territory.service";
import { TerritoryGeometryDrawer } from "./TerritoryGeometryDrawer";
import { TerritorySelect } from "./TerritorySelect";

interface TerritoryFormModalProps {
	open: boolean;
	onClose: () => void;
	organizationId: string;
	territory?: Territory | null;
	defaultParentId?: string | null;
}

interface FormValues {
	name: string;
	parentId?: string | null;
}

export function TerritoryFormModal({
	open,
	onClose,
	organizationId,
	territory,
	defaultParentId,
}: TerritoryFormModalProps) {
	const [form] = Form.useForm<FormValues>();
	const isEdit = !!territory;

	const [geometry, setGeometry] = useState<TerritoryCoordinates | undefined>(
		territory?.coordinates ?? undefined
	);
	const [metrics, setMetrics] = useState<{ area: number; perimeter: number }>({
		area: territory?.area ?? 0,
		perimeter: territory?.perimeter ?? 0,
	});

	const { mutateAsync: createTerritory, isPending: isCreating } = useCreateTerritory();
	const { mutateAsync: updateTerritory, isPending: isUpdating } = useUpdateTerritory();
	const isSubmitting = isCreating || isUpdating;

	useEffect(() => {
		if (open) {
			if (territory) {
				form.setFieldsValue({
					name: territory.name,
					parentId: territory.parentId ?? undefined,
				});
				setGeometry(territory.coordinates ?? undefined);
				setMetrics({
					area: territory.area ?? 0,
					perimeter: territory.perimeter ?? 0,
				});
			} else {
				form.resetFields();
				if (defaultParentId) {
					form.setFieldValue("parentId", defaultParentId);
				}
				setGeometry(undefined);
				setMetrics({ area: 0, perimeter: 0 });
			}
		}
	}, [open, territory, defaultParentId, form]);

	const handleGeometryChange = (
		newGeometry: TerritoryCoordinates | undefined,
		newMetrics: { area: number; perimeter: number }
	) => {
		setGeometry(newGeometry);
		setMetrics(newMetrics);
	};

	const handleFinish = async (values: FormValues) => {
		try {
			if (isEdit && territory) {
				await updateTerritory({
					id: territory.id,
					data: {
						name: values.name.trim(),
						parentId: values.parentId || null,
						coordinates: geometry || null,
						area: metrics.area || null,
						perimeter: metrics.perimeter || null,
					},
				});
			} else {
				await createTerritory({
					name: values.name.trim(),
					organizationId,
					parentId: values.parentId || null,
					coordinates: geometry || null,
					area: metrics.area || null,
					perimeter: metrics.perimeter || null,
				});
			}
			onClose();
		} catch {
			// error handled by mutation
		}
	};

	return (
		<Modal
			title={isEdit ? "Hududni tahrirlash" : "Yangi hudud yaratish"}
			open={open}
			onCancel={onClose}
			onOk={() => form.submit()}
			confirmLoading={isSubmitting}
			okText={isEdit ? "Saqlash" : "Yaratish"}
			cancelText="Bekor qilish"
			width={800}
			destroyOnClose
		>
			<Form form={form} layout="vertical" onFinish={handleFinish} className="mt-4">
				<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
					<Form.Item
						name="name"
						label="Hudud nomi"
						rules={[{ required: true, message: "Hudud nomini kiriting" }]}
					>
						<Input placeholder="Masalan: 1-hudud" maxLength={255} />
					</Form.Item>

					<Form.Item name="parentId" label="Yuqori hudud (ixtiyoriy)">
						<TerritorySelect
							organizationId={organizationId}
							excludeId={territory?.id}
							placeholder="Asosiy hudud (tanlanmasa)"
							allowClear
						/>
					</Form.Item>
				</div>

				<div className="space-y-1 mt-2">
					<div className="text-sm font-medium text-gray-700 block">
						Hudud chegarasi (xaritada chizish)
					</div>
					<TerritoryGeometryDrawer value={geometry} onChange={handleGeometryChange} height={360} />
				</div>
			</Form>
		</Modal>
	);
}
